import { useContext, useState } from 'react'
import { UserContext } from 'contexts/user'
/* eslint-disable no-nested-ternary */
import { trackEvent } from 'analytics/trackFunctions'
import { useNavigate, useParams } from 'react-router-dom'

import { URL_RENOVADOR } from 'config'
import { CREDIT_PRODUCT } from 'api/advances'
import { MAX_ADVANCE_AMOUNT } from 'constants/credit'
import { ADVANCE_BANNER_STEPS, LOAN_BANNER_STEPS } from 'constants/wording'

// Components
import PageWrapper from 'components/PageWrapper'
import { ProgressInfo } from 'components/creditScore/CreditLines/ProgressInfo'
import PrimaryButton from 'components/primaryButton'
import LoanDetailButton from 'components/AdvanceDetails'
import {
  useActiveCreditsStore,
  useLineSummaryStore,
  useLoanInfoStore
} from 'store'
import { HelpIcon } from 'assets/icons'
import { DepletedLineModal } from 'components/LineModals'
import TickIcon from 'components/listbox/SelectedIcon'
import { toLocaleCurrency } from 'utils/dinero'

const { WALLET, LOANS } = CREDIT_PRODUCT

const typeParam = {
  [WALLET]: 'adelantos',
  [LOANS]: 'prestamos'
}

const LineDetail = () => {
  const navigate = useNavigate()
  const { type } = useParams()
  const { user } = useContext(UserContext)

  const { updateLoanInfo } = useLoanInfoStore()
  const { activeAdvances, activeLoans } = useActiveCreditsStore()

  const renderBanner = () => {
    if (type === typeParam[LOANS]) {
      return (
        <Banner
          title='Comprá eso que querés con un préstamo a la mejor tasa'
          steps={LOAN_BANNER_STEPS}
        />
      )
    }

    return (
      <Banner
        title={`Salí del apuro con hasta $${toLocaleCurrency(
          MAX_ADVANCE_AMOUNT
        )}`}
        steps={ADVANCE_BANNER_STEPS}
      />
    )
  }

  const renderContent = () => {
    const activeCredits =
      type === typeParam[LOANS] ? activeLoans : activeAdvances

    if (activeCredits?.length > 0) {
      return activeCredits?.map((item) => (
        <LoanDetailButton
          action={() => {
            updateLoanInfo(item)
            navigate('/wayni-credito/detalle')
          }}
          className='my-2'
          loanData={item}
          key={item.uuid}
        />
      ))
    }

    return renderBanner()
  }

  return (
    <PageWrapper
      noTabBar
      title={type === typeParam[LOANS] ? 'Tus préstamos' : 'Tus adelantos'}
    >
      <section className='flex flex-col text-black items-center sm:max-w-md pb-6 mx-auto h-full'>
        <h1 className='font-bold text-xl self-start text-left'>
          Línea de crédito
        </h1>

        <ProgressDetail
          type={type === typeParam[LOANS] ? 'LOANS' : 'WALLET'}
          className='mt-4 mb-2'
        />

        {renderContent()}

        <PrimaryButton
          className='fixed shadow-navMob bottom-8 !w-[90vw] md:max-w-md md:static md:shadow-none md:mt-2'
          buttonText={
            type === typeParam[LOANS] ? 'Simular préstamo' : 'Simular adelanto'
          }
          primaryButtonFunction={() => {
            if (type === typeParam[LOANS]) {
              trackEvent({ eventName: 'bille_boton_simular_prestamo' })
              window
                .open(
                  `${URL_RENOVADOR}iniciando?access_token=${user.access_token}`,
                  '_blank'
                )
                .focus()
            } else {
              trackEvent({ eventName: 'bille_boton_simular_adelanto' })
              navigate('/adelantos/tipo-de-adelanto')
            }
          }}
        />
      </section>
    </PageWrapper>
  )
}

export default LineDetail

const ProgressDetail = ({
  className = '',
  type
}: {
  className?: string
  type: keyof typeof CREDIT_PRODUCT
}) => {
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false)
  const { lineSummary } = useLineSummaryStore()
  const { activeAdvances, activeLoans } = useActiveCreditsStore()

  const allocatedAmountFromOtherPeriod =
    CREDIT_PRODUCT[type] === LOANS
      ? activeLoans?.reduce(
          (acc, item) =>
            acc +
            (item.was_requested_in_last_credit_line
              ? 0
              : item.delivered_amount),
          0
        )
      : activeAdvances?.reduce(
          (acc, item) =>
            acc +
            (item.was_requested_in_last_credit_line
              ? 0
              : item.delivered_amount),
          0
        ) ?? 0

  const allocatedAmount =
    (CREDIT_PRODUCT[type] === LOANS
      ? lineSummary.loanAllocatedAmount
      : lineSummary.advanceAllocatedAmount) + allocatedAmountFromOtherPeriod
  const approvedAmount =
    (CREDIT_PRODUCT[type] === LOANS
      ? lineSummary.loanApprovedAmount
      : lineSummary.advanceApprovedAmount) + allocatedAmountFromOtherPeriod
  const remainingAmount =
    CREDIT_PRODUCT[type] === LOANS
      ? lineSummary.loanRemainingAmount
      : lineSummary.advanceRemainingAmount

  const percentage = Math.ceil((remainingAmount * 100) / approvedAmount)

  return (
    <>
      <div
        className={`bg-white gap-4 flex items-center py-5 px-4 w-full shadow-cardBanner rounded-[10px] ${className}`}
      >
        <div className='rounded-full shrink-0 font-bold justify-center leading-none flex flex-col items-center border-[3px] text-green-800 border-green-800 w-[4.75rem] h-[4.75rem]'>
          <span className='text-2xl'>{percentage}%</span>
          <span className='text-2xs'>disponible</span>
        </div>
        <ProgressInfo
          className='!text-2xs !text-gray-900'
          barBgClassName='!bg-green-800'
          barClassName='h-2'
          allocated={allocatedAmount}
          approved={approvedAmount}
        />
        {remainingAmount <= 0 && (
          <button
            type='button'
            onClick={() => setModalIsOpen(true)}
            className={`flex shrink-0 justify-center items-center w-6 h-6 rounded-full
     }`}
          >
            <p className='sr-only'>?</p>
            <HelpIcon variant='GREEN' className='h-5 w-5' />
          </button>
        )}
      </div>
      <DepletedLineModal
        isLoan={CREDIT_PRODUCT[type] === LOANS}
        isOpen={modalIsOpen}
        setIsOpen={setModalIsOpen}
      />
    </>
  )
}

const Banner = ({ title, steps }) => (
  <div className='bg-white mt-2 shadow-cardBanner w-full rounded-[10px] py-5 px-4'>
    <h2 className='text-left text-black font-bold px-2 text-xl leading-5'>
      {title}
    </h2>
    <div className='space-y-2 mt-4 px-2 self-start'>
      {steps.map((item) => (
        <div key={item.number} className='flex gap-2 items-center '>
          <TickIcon className='w-4 h-4' />
          <p className='text-black text-left font-medium text-xs'>
            {item.description}
          </p>
        </div>
      ))}
    </div>
  </div>
)
